import { DeltaAction, type DeltaCreate, type InsuredOnDateResponse } from '../../api';
import { isValidCpf, parseBrazilianAmount, stripCpf } from '../../format';

/**
 * One requested change, as the form holds it and as the API takes it.
 *
 * The form is one set of fields with an action at the top, not four forms side
 * by side, and this module is where that stops being a layout decision. An
 * action decides which fields are required, which are ignored, and what the
 * request body looks like -- and the body it produces is one of the API's four
 * shapes, which each forbid the other three's fields outright (#210, #284).
 * There is no arrangement of this form that sends a termination carrying a
 * salary, or an afastamento carrying either.
 *
 * Two facts are deliberately absent and must stay absent: `subestipulante` and
 * `modulo`. They belong to the contract (#209), the API has no per-change field
 * for them, and asking a customer to retype the insurer's codes on every line
 * is the spreadsheet habit this product exists to end.
 */

export interface ChangeFormState {
  readonly action: DeltaAction | '';
  /** `insured_person_id` of the chosen person. Terminations and salary changes. */
  readonly personId: string;
  /** Admissions only; the other two take the CPF from the person they name. */
  readonly cpf: string;
  readonly fullName: string;
  readonly birthDate: string;
  readonly effectiveDate: string;
  /**
   * Afastamentos only: the last day away, when it is already known.
   *
   * Empty is a real answer rather than a missing one -- an absence whose return
   * date nobody knows yet is the ordinary case -- so it is sent as an absent
   * field rather than as an empty string, and the leave stays open until a
   * later request completes it.
   */
  readonly leaveEndDate: string;
  /** As typed, in pt-BR. Parsed on submission, never as it is typed. */
  readonly salary: string;
  /** Signed paperwork, for a termination that has it to hand. */
  readonly file: File | null;
}

export const EMPTY_CHANGE_FORM: ChangeFormState = {
  action: '',
  personId: '',
  cpf: '',
  fullName: '',
  birthDate: '',
  effectiveDate: '',
  leaveEndDate: '',
  salary: '',
  file: null,
};

/** Which actions name somebody the roster already knows, rather than introducing one. */
export function actsOnExistingPerson(action: DeltaAction | ''): boolean {
  return (
    action === DeltaAction.TERMINATION ||
    action === DeltaAction.SALARY_CHANGE ||
    action === DeltaAction.LEAVE
  );
}

export function collectsIdentity(action: DeltaAction | ''): boolean {
  return action === DeltaAction.ADMISSION;
}

export function collectsSalary(action: DeltaAction | ''): boolean {
  return action === DeltaAction.ADMISSION || action === DeltaAction.SALARY_CHANGE;
}

/** Only a termination has signed paperwork to carry at the moment it is asked for. */
export function collectsDocument(action: DeltaAction | ''): boolean {
  return action === DeltaAction.TERMINATION;
}

/**
 * Only an afastamento has a second date, and it is optional.
 *
 * The field is shown for it and for nothing else: the API forbids
 * `leave_end_date` on every other action outright, so an input that appeared
 * beside a termination would be an input whose value could never be sent.
 */
export function collectsLeaveEndDate(action: DeltaAction | ''): boolean {
  return action === DeltaAction.LEAVE;
}

const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;

export type ChangeFormErrors = Record<string, string>;

export interface ValidChange {
  readonly delta: DeltaCreate['delta'];
}

/**
 * Whether the form can be sent, and the body to send.
 *
 * The keys of the error map are the API's own field names, so a refusal from
 * the server and a refusal from here land on the same input. There is no key
 * for a date that is too far ahead, and there must never be one: a change
 * effective in a future month is the ordinary case this product is built
 * around, and the API accepts it without comment.
 */
export function validateChange(
  state: ChangeFormState,
  roster: readonly InsuredOnDateResponse[],
): { errors: ChangeFormErrors } | ValidChange {
  const errors: ChangeFormErrors = {};

  if (state.action === '') {
    return { errors: { action: 'Escolha o que você quer fazer.' } };
  }

  if (state.effectiveDate === '') {
    errors['effective_date'] = 'Informe a data de vigência.';
  } else if (!CALENDAR_DATE.test(state.effectiveDate)) {
    errors['effective_date'] = 'Informe uma data válida.';
  }

  const person = roster.find((entry) => entry.insured_person_id === state.personId);
  if (actsOnExistingPerson(state.action) && person === undefined) {
    errors['person'] = 'Escolha a pessoa no cadastro.';
  }

  // Blank is a real answer here -- "we do not know when they come back" -- so
  // only a value that is there and wrong is refused.
  const hasLeaveEnd = collectsLeaveEndDate(state.action) && state.leaveEndDate !== '';
  if (hasLeaveEnd) {
    if (!CALENDAR_DATE.test(state.leaveEndDate)) {
      errors['leave_end_date'] = 'Informe uma data válida.';
    } else if (state.leaveEndDate < state.effectiveDate) {
      errors['leave_end_date'] = 'O último dia não pode ser antes do primeiro.';
    }
  }

  let salary: string | undefined;
  if (collectsSalary(state.action)) {
    salary = parseBrazilianAmount(state.salary);
    if (salary === undefined) {
      errors['salary'] = 'Informe o salário. Use vírgula para os centavos.';
    }
  }

  if (collectsIdentity(state.action)) {
    if (!isValidCpf(state.cpf)) {
      errors['cpf'] = 'Informe um CPF válido, com onze dígitos.';
    }
    if (state.fullName.trim() === '') {
      errors['full_name'] = 'Informe o nome completo da pessoa.';
    }
    if (!CALENDAR_DATE.test(state.birthDate)) {
      errors['birth_date'] = 'Informe a data de nascimento.';
    }
  }

  if (Object.keys(errors).length > 0) {
    return { errors };
  }

  // The CPF of an existing person comes from the roster, not from the form.
  // Retyping it is how somebody ends up terminating the wrong employee.
  switch (state.action) {
    case DeltaAction.ADMISSION:
      return {
        delta: {
          action: DeltaAction.ADMISSION,
          cpf: stripCpf(state.cpf),
          effective_date: state.effectiveDate,
          full_name: state.fullName.trim(),
          birth_date: state.birthDate,
          salary: salary as string,
        },
      };
    case DeltaAction.TERMINATION:
      return {
        delta: {
          action: DeltaAction.TERMINATION,
          cpf: (person as InsuredOnDateResponse).cpf,
          effective_date: state.effectiveDate,
        },
      };
    case DeltaAction.SALARY_CHANGE:
      return {
        delta: {
          action: DeltaAction.SALARY_CHANGE,
          cpf: (person as InsuredOnDateResponse).cpf,
          effective_date: state.effectiveDate,
          salary: salary as string,
        },
      };
    case DeltaAction.LEAVE:
      return {
        delta: {
          action: DeltaAction.LEAVE,
          cpf: (person as InsuredOnDateResponse).cpf,
          effective_date: state.effectiveDate,
          // Omitted rather than sent as null when it is blank: an absent field
          // and "we do not know yet" are the same statement, and the API reads
          // both the same way.
          ...(hasLeaveEnd ? { leave_end_date: state.leaveEndDate } : {}),
        },
      };
  }
}
